import React, {useState} from 'react';
import Box from 'sail/Box';
import Group from 'sail/Group';
import {Title, Intro} from 'sail/Text';
import {TextArea} from 'sail/Input';
import Button from 'sail/Button';
import Checkbox from 'sail/Checkbox';
import {Toast} from 'sail/Toast';
import {defineMessages, FormattedMessage} from 'react-intl';
import {useAccount, useApi} from 'docs/data';
import {sanitizePII, wait} from 'docs/util';

const FORM_STATE_INITIAL = 'initial';
const FORM_STATE_SUBMITTING = 'submitting';
const FORM_STATE_DID_SUBMIT = 'did-submit';
const FORM_STATE_DID_EXIT = 'did-exit';

const messages = defineMessages({
  thanks: {
    id: 'docsApp.integrationBuilder.feedback.thanks',
    description: 'Thanks message',
    defaultMessage: 'Thank you for your feedback!',
  },
  feedback: {
    id: 'docsApp.integrationBuilder.feedback.feedback',
    description: 'Feedback label',
    defaultMessage: 'Feedback',
  },
  whatDidYouThink: {
    id: 'docsApp.integrationBuilder.feedback.whatDidYouThink',
    description: 'Soliciting feedback',
    defaultMessage: 'What did you think of this new guide format?',
  },
  followUp: {
    id: 'docsApp.integrationBuilder.feedback.followUp',
    description: 'Follow up message',
    defaultMessage: 'Stripe can follow up with me via email',
  },
  submit: {
    id: 'docsApp.integrationBuilder.feedback.submit',
    description: 'Submit button label',
    defaultMessage: 'Submit',
  },
});

export const Feedback = () => {
  const [text, setText] = useState('');
  const [canContact, setCanContact] = useState(false);
  const [formState, setFormState] = useState(FORM_STATE_INITIAL);
  const {account} = useAccount();
  const {api} = useApi();

  const handleSubmit = () => {
    if (text.trim().length < 1) {
      return;
    }

    const payload = {
      text: sanitizePII(text, '[REDACTED]'),
      contact: canContact,
    };

    payload.csrf_token = '1';
    payload.path = window.location.pathname;

    // Send to CSAT endpoint to pipe to Slack channel
    setFormState(FORM_STATE_SUBMITTING);
    return api
      .sendCsatFeedback(payload)
      .catch(() => null)
      .then(() => {
        setFormState(FORM_STATE_DID_SUBMIT);
        return 2000;
      })
      .then(wait)
      .then(() => {
        setFormState(FORM_STATE_DID_EXIT);
      });
  };

  const handleKeyDown = (e) => {
    const {key, metaKey} = e;
    if (key === 'Enter' && metaKey) {
      handleSubmit();
    }
  };

  const attachKeyHandler = () => {
    document.addEventListener('keydown', handleKeyDown);
  };

  const detachKeyHandler = () => {
    document.removeEventListener('keydown', handleKeyDown);
  };

  const containerProps = {
    style: {minHeight: 215},
  };
  if (formState === FORM_STATE_DID_EXIT) {
    return <Box {...containerProps} />;
  }
  if (formState === FORM_STATE_DID_SUBMIT) {
    return (
      <Box {...containerProps}>
        <Toast icon="checkmark" color="green">
          <FormattedMessage {...messages.thanks} />
        </Toast>
      </Box>
    );
  }

  return (
    <Box margin={{horizontal: 32, top: 48}}>
      <Group {...containerProps} spacing={16}>
        <Title color="dark" style={{fontWeight: 700}}>
          <FormattedMessage {...messages.feedback} />
        </Title>
        <Intro>
          <FormattedMessage {...messages.whatDidYouThink} />
        </Intro>
        <TextArea
          onChange={(val) => {
            setText(val);
          }}
          placeholder=""
          resizeable={false}
          value={text}
          width="maximized"
          onFocus={attachKeyHandler}
          onBlur={detachKeyHandler}
        />
        <Group
          direction="horizontal"
          spacing={32}
          alignItems="center"
          justifyContent="space-between"
        >
          {account.merchant?.isLoggedIn && (
            <Checkbox
              checked={canContact}
              onChange={({target}) => {
                setCanContact(target.checked);
              }}
              label={<FormattedMessage {...messages.followUp} />}
            />
          )}
          <Button
            label={<FormattedMessage {...messages.submit} />}
            color="white"
            onClick={handleSubmit}
            pending={formState === FORM_STATE_SUBMITTING}
          />
        </Group>
      </Group>
    </Box>
  );
};
